import { NgStyle } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import { IVideoPlayedContext, IVideoPlayerConfiguration } from './video-player.interface';

@Component({
  imports: [NgStyle, TranslateModule, IconComponent],
  selector: 'app-video-player',
  styleUrl: './video-player.component.scss',
  templateUrl: './video-player.component.html',
})
export class VideoPlayerComponent implements AfterViewInit {
  public readonly configuration = input.required<IVideoPlayerConfiguration>();
  public readonly seekTime = model<number | null>(null);
  public readonly protectHeight = input<boolean>(false);
  /** For campaign_analytics_content_video_played / creator_content_video_played. Omit to not track. */
  public readonly videoPlayedContext = input<IVideoPlayedContext | null>(null);

  private readonly eventTrackingService = inject(EventTrackingService);

  public readonly videoElement = viewChild<ElementRef<HTMLVideoElement>>('videoPlayer');

  public readonly hasBeenPlayed = signal<boolean>(false);
  public readonly showCustomPlayButton = computed(() => !this.hasBeenPlayed());

  public readonly videoStyles = computed(() => {
    const config = this.configuration();
    const styles: Record<string, string> = {};

    if (config.width) {
      styles['width'] = `${config.width}px`;
    }

    if (config.height) {
      styles['height'] = `${config.height}px`;
    }

    if (this.protectHeight() && this.videoPlayerHeight()) {
      styles['height'] = `${this.videoPlayerHeight() ?? 0}px`;
    }

    return styles;
  });

  public videoPlayerHeight = signal<number | null>(null);

  private readonly desiredSeek = signal<number | null>(null);

  private lastSrc: string | null = null;
  private sourceChanged = false;

  constructor() {
    effect(() => {
      const seekTime = this.seekTime();
      this.seekTime.set(null);

      if (seekTime) {
        this.desiredSeek.set(seekTime);
      }
    });

    effect(() => {
      const currentSrc = this.configuration().src;
      const desiredSeek = this.desiredSeek();

      if (typeof desiredSeek === 'number' && currentSrc) {
        this.sourceChanged = currentSrc !== this.lastSrc;
        this.lastSrc = currentSrc;

        this.seekTo(desiredSeek);
      }
    });
  }

  public ngAfterViewInit(): void {
    const videoEl = this.videoElement()?.nativeElement;

    if (!videoEl) {
      return;
    }

    videoEl.volume = 0.5;

    // Set volume to 50% on first unmute (browser default is 1.0)
    const onFirstUnmute = (): void => {
      if (!videoEl.muted && videoEl.volume === 1) {
        videoEl.volume = 0.5;
        videoEl.removeEventListener('volumechange', onFirstUnmute);
      }
    };
    videoEl.addEventListener('volumechange', onFirstUnmute);

    // Listen for first play to show controls. Also covers onCustomPlayButtonClick() below, since
    // calling videoEl.play() from there triggers this same native 'play' event — tracking here
    // once avoids double-firing for the custom-button path.
    videoEl.addEventListener('play', () => {
      // 'play' fires again on every resume-from-pause, not just the first play - only track once
      // per source (loadstart resets hasBeenPlayed below).
      const isFirstPlay = !this.hasBeenPlayed();
      this.hasBeenPlayed.set(true);

      if (isFirstPlay) {
        this.trackVideoPlayed();
      }
    });

    // Reset state when video source changes
    videoEl.addEventListener('loadstart', () => {
      this.hasBeenPlayed.set(false);
    });

    // Update height when video is ready
    videoEl.addEventListener('canplay', () => {
      const el = this.videoElement()?.nativeElement;

      if (!el) {
        return;
      }

      setTimeout(() => {
        if (this.sourceChanged) {
          this.sourceChanged = false;
          this.seekTo(this.desiredSeek() ?? 0);
        }

        this.videoPlayerHeight.set(el.clientHeight);
      });
    });
  }

  public onCustomPlayButtonClick(): void {
    const videoEl = this.videoElement()?.nativeElement;

    if (videoEl) {
      // Don't set hasBeenPlayed here - the native 'play' listener (ngAfterViewInit) is the single
      // source of truth for it. play() fires 'play' asynchronously (a queued task, not synchronous),
      // so setting it eagerly here made that listener's isFirstPlay check always see it as already
      // true, and trackVideoPlayed() never fired for this (the only pre-first-play) path.
      videoEl.play();
    }
  }

  private trackVideoPlayed(): void {
    const context = this.videoPlayedContext();

    if (!context) {
      return;
    }

    this.eventTrackingService.track(context.eventType, {
      analyticsPopupActive: context.analyticsPopupActive,
    });
  }

  private seekTo(seconds: number): void {
    const videoEl = this.videoElement()?.nativeElement;

    if (!videoEl) {
      return;
    }

    // If metadata not ready yet, wait once
    if (videoEl.readyState < 1) {
      const onMeta = (): void => {
        videoEl.currentTime = Math.max(0, seconds);
        videoEl.removeEventListener('loadedmetadata', onMeta);
      };
      videoEl.addEventListener('loadedmetadata', onMeta, { once: true });
    } else {
      videoEl.currentTime = Math.max(0, seconds);
    }
  }
}
